import { useState } from 'react';
import {
  filterOutGroupById,
  getGroupBudgetsPayload,
} from 'src/components/oneCorp/features/MealCredit/hooks/assignAllowanceAmount.utils';
import { updateBudgetsForMealCredit } from 'src/components/oneCorp/features/MealCredit/hooks/updateBudgetsForMealCreditApi';
import { useAsyncTaskHandler } from 'src/common/utils/useAsyncTaskHandler';
import { LocTagInvite } from 'src/types/LineOfCredit';

export interface UseManageMealCreditGroups {
  addGroup: (newGroup: LocTagInvite) => Promise<void>;
  replaceGroup: (
    newGroup: LocTagInvite,
    originalGroupIdEdited: string
  ) => Promise<void>;
  removeGroup: (groupToRemove: LocTagInvite) => Promise<void>;
  status: ManageMealCreditGroupStatus;
  isRemoving: boolean;
  isAdding: boolean;
  isUpdating: boolean;
  errors: string[];
}

export interface UseManageMealCreditGroupsProps {
  corpId: string;
  mealCreditId: string;
  tagInvites: LocTagInvite[];
  onSuccess: () => Promise<void>;
  onError: () => Promise<void>;
}

export enum ManageMealCreditGroupStatus {
  IDLE,
  ADDING,
  UPDATING,
  REMOVING,
}

export const useManageMealCreditGroups = ({
  corpId,
  mealCreditId,
  tagInvites = [],
  onError,
  onSuccess,
}: UseManageMealCreditGroupsProps): UseManageMealCreditGroups => {
  const { startAsyncTask, errors } = useAsyncTaskHandler({
    onSuccess: handleSuccess,
    onError: handleError,
  });
  const [status, setStatus] = useState<ManageMealCreditGroupStatus>(
    ManageMealCreditGroupStatus.IDLE
  );

  const isRemoving = status === ManageMealCreditGroupStatus.REMOVING;
  const isAdding = status === ManageMealCreditGroupStatus.ADDING;
  const isUpdating = status === ManageMealCreditGroupStatus.UPDATING;

  async function addGroup(newGroup: LocTagInvite) {
    setStatus(ManageMealCreditGroupStatus.ADDING);

    const updatedGroupBudgets = getGroupBudgetsPayload([
      ...tagInvites,
      newGroup,
    ]);
    await updateGroupBudgets(updatedGroupBudgets);
  }

  async function replaceGroup(
    newGroup: LocTagInvite,
    originalGroupIdEdited: string
  ) {
    // newGroup.id and originalGroupIdEdited can differ when the user changes which group an existing budget amount is assigned to

    setStatus(ManageMealCreditGroupStatus.UPDATING);

    const filteredGroupBudgets = filterOutGroupById(
      tagInvites,
      originalGroupIdEdited
    );

    const updatedGroupBudgets = getGroupBudgetsPayload([
      ...filteredGroupBudgets,
      newGroup,
    ]);

    await updateGroupBudgets(updatedGroupBudgets);
  }

  async function removeGroup(groupToRemove: LocTagInvite) {
    setStatus(ManageMealCreditGroupStatus.REMOVING);

    const filteredGroupBudgets = filterOutGroupById(
      tagInvites,
      groupToRemove.id
    );
    const updatedGroupBudgets = getGroupBudgetsPayload(filteredGroupBudgets);
    await updateGroupBudgets(updatedGroupBudgets);
  }

  async function updateGroupBudgets(
    updatedGroupBudgets: Record<string, number>
  ): Promise<void> {
    await startAsyncTask(() => {
      return updateBudgetsForMealCredit({
        corpId,
        mealCreditId,
        updatedGroupBudgets,
      });
    });
  }

  async function handleSuccess(): Promise<void> {
    await onSuccess();
    setStatus(ManageMealCreditGroupStatus.IDLE);
  }

  async function handleError(): Promise<void> {
    setStatus(ManageMealCreditGroupStatus.IDLE);
    await onError();
  }

  return {
    addGroup,
    replaceGroup,
    removeGroup,
    status,
    isAdding,
    isRemoving,
    isUpdating,
    errors,
  };
};
